/** @shell standard @header bar */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert, Platform } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { ButtonLoader } from '../components/load-main/ButtonLoader';
import { Clickable } from '../components/common/Clickable';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Input } from '../components/common/Input';
import { AppPickerTrigger } from '../components/common/AppPickerTrigger';
import { AreaPickerModal } from '../components/common/AreaPickerModal';
import { useRoute, useNavigation, type RouteProp } from '@react-navigation/native';
import type { MainStackParamList } from '../navigation/types';
import { getSelectedSubscription } from '../data/domains/subscription';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { useCreateAddress, useUpdateAddress, useAreas, fetchDeliverySlots, coerceAddressIsDefault } from '../data/domains/addresses';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import type { AddressPayload } from '../data/domains/addresses';
import { useAuthStore } from '../shared/auth/store';
import { useTranslation, getAreaBilingualLabel } from '../shared/i18n';
import { useOrganizationSettings, isAddressFormFieldVisible, isAddressFormFieldRequired, extractAsciiPhoneDigits } from '../data/domains/config';
import { toAsciiDigits } from '../shared/utils/apiParams';
import moment from 'moment';
import { sharedStyles } from '../shared/theme/sharedStyles';
import { createPageFooterColoredCtaStyles } from '../shared/theme/styles/buttons';
import { useAuthSheetKeyboardInset } from '../shared/auth';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioSectionCardStyle,
  androidScaleFont,
  androidScaleSize,
  androidFlatSurfaceStyle,
} from '../shared/theme/studio/stackScreenShell';

const FOOTER_VERTICAL_PAD = appTheme.spacing.md;
const FOOTER_CTA_HEIGHT = androidScaleSize(52);

function footerTotalHeight(bottom_inset: number): number {
  return FOOTER_VERTICAL_PAD * 2 + FOOTER_CTA_HEIGHT + bottom_inset;
}

const layoutStyles = StyleSheet.create({
  keyboardWrap: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: STACK_SCROLL_GUTTER,
  },
});

function createAddressFormStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  return StyleSheet.create({
    screenRoot: {
      flex: 1,
    },
    contentWrap: {
      flex: 1,
    },
    scrollRoot: {
      flex: 1,
    },
    formSection: studioSectionCardStyle(c, {
      marginTop: STACK_SECTION_GAP,
      paddingHorizontal: appTheme.spacing.lg,
      paddingVertical: appTheme.spacing.lg,
    }),
    sectionTitle: {
      fontSize: sf(12),
      color: c.textLight,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: appTheme.spacing.md,
      fontWeight: '600',
    },
    fieldGroup: {
      marginBottom: appTheme.spacing.xl,
    },
    row: {
      flexDirection: 'row',
      gap: appTheme.spacing.sm,
      alignItems: 'flex-start',
    },
    rowInput: {
      flex: 1,
      minWidth: 0,
    },
    pickerButtonWrap: {},
    pickerDropdown: {
      backgroundColor: c.surface,
      borderRadius: appTheme.borderRadius.sm,
      marginTop: appTheme.spacing.xs,
      borderWidth: 1,
      borderColor: c.border,
      maxHeight: 200,
      ...appTheme.shadows.sm,
      shadowColor: c.shadowColor,
    },
    pickerScroll: {
      maxHeight: 200,
    },
    pickerOptionWrap: {},
    pickerOption: {
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: appTheme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: c.borderLight,
    },
    pickerOptionSelected: {
      backgroundColor: c.primaryLight,
    },
    pickerOptionText: {
      ...appTheme.typography.body,
      color: c.text,
    },
    pickerOptionTextSelected: {
      color: c.primary,
      fontWeight: '600',
    },
    defaultToggleWrap: { marginTop: appTheme.spacing.xl },
    defaultToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: c.surface,
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: 16,
      borderRadius: appTheme.borderRadius.md,
      borderWidth: 1,
      borderColor: c.border,
    },
    checkbox: {
      width: 24,
      height: 24,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: c.border,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: appTheme.spacing.md,
    },
    checkboxChecked: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    defaultToggleText: {
      ...appTheme.typography.body,
      color: c.text,
    },
    pageFooter: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: FOOTER_VERTICAL_PAD,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      backgroundColor: c.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight,
      ...Platform.select({
        ios: { ...appTheme.shadows.lg, shadowColor: c.shadowColor },
        android: androidFlatSurfaceStyle(),
      }),
    },
    pageFooterCtaWrap: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    footerBtnWrap: {
      alignSelf: 'stretch',
      width: '100%',
    },
    footerBtnInnerFull: {
      alignSelf: 'stretch',
      width: '100%',
    },
  });
}

interface AddressFormData {
  name: string;
  area_id: string;
  slot_id: string;
  governorate_id: string;
  block: string;
  street: string;
  house_name: string;
  avenue: string;
  floor: string;
  flat: string;
  landmark: string;
  phone: string;
  driver_note: string;
  PACI_number: string;
  zip_code: string;
  is_default: boolean;
}

interface DeliverySlot {
  id: number;
  name?: string;
  start_time: string;
  end_time: string;
  delivery_start_time?: string;
  delivery_end_time?: string;
}

export const AddressFormScreen: React.FC = () => {
  const route = useRoute<RouteProp<MainStackParamList, 'AddressForm'>>();
  const navigation = useAuthNavigation();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createAddressFormStyles(themeColors), [themeColors]);
  const colored_footer_cta = useMemo(() => createPageFooterColoredCtaStyles(themeColors), [themeColors]);
  const user = useAuthStore(s => s.user);
  const on_back_exit = useExitOrGoBack();
  const editAddress = route.params?.address;
  const after_save_go_checkout = route.params?.afterSaveGoTo === 'Checkout';
  const isEditing = !!editAddress;
  const header_top_padding = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const keyboard_inset = useAuthSheetKeyboardInset();
  const footer_total_height = footerTotalHeight(insets.bottom);
  const scroll_padding_bottom = useMemo(
    () => footer_total_height + appTheme.spacing.lg + (Platform.OS === 'android' ? keyboard_inset : 0),
    [footer_total_height, keyboard_inset],
  );
  const footer_bottom_offset = keyboard_inset > 0 ? keyboard_inset : 0;

  const createAddressMutation = useCreateAddress();
  const updateAddressMutation = useUpdateAddress();

  const { data: areas, isLoading: areasLoading, error: areasError, refetch: refetchAreas } = useAreas();

  useEffect(() => {}, [areas, areasLoading]);

  const [formData, setFormData] = useState<AddressFormData>({
    name: '',
    area_id: '',
    slot_id: '',
    governorate_id: '',
    block: '',
    street: '',
    house_name: '',
    avenue: '',
    floor: '',
    flat: '',
    landmark: '',
    phone: '',
    driver_note: '',
    PACI_number: '',
    zip_code: '',
    is_default: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showAreaPicker, setShowAreaPicker] = useState(false);
  const [showSlotPicker, setShowSlotPicker] = useState(false);
  const [deliverySlots, setDeliverySlots] = useState<DeliverySlot[]>([]);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  useOrganizationSettings();
  const show_field = isAddressFormFieldVisible;

  useEffect(() => {
    if (editAddress) {
      setFormData({
        name: editAddress.name || '',
        area_id: editAddress.area_id ? String(editAddress.area_id) : '',
        slot_id: editAddress.slot_id ? String(editAddress.slot_id) : '',
        governorate_id: editAddress.governorate_id ? String(editAddress.governorate_id) : '',
        block: editAddress.block || '',
        street: editAddress.street || '',
        house_name: editAddress.house_name || '',
        avenue: editAddress.avenue || '',
        floor: editAddress.floor || '',
        flat: editAddress.flat || '',
        landmark: editAddress.landmark || '',
        phone: toAsciiDigits(editAddress.phone || ''),
        driver_note: editAddress.driver_note || '',
        PACI_number: editAddress.PACI_number || '',
        zip_code: editAddress.zip_code || '',
        is_default: coerceAddressIsDefault(editAddress.is_default),
      });
      if (editAddress.area_id) {
        loadDeliverySlots(editAddress.area_id);
      }
    }
  }, [editAddress]);

  const loadDeliverySlots = async (areaId: string | number) => {
    try {
      const slots = await fetchDeliverySlots(areaId);
      const formatted = slots.map(slot => ({
        ...slot,
        delivery_start_time: moment(slot.start_time, 'HH:mm:ss').format('hh:mm A'),
        delivery_end_time: moment(slot.end_time, 'HH:mm:ss').format('hh:mm A'),
      }));
      setDeliverySlots(formatted as DeliverySlot[]);
    } catch (_error) {
      // ignore slot load failures
    }
  };

  const onAreaChange = useCallback((area: any) => {
    const governorate_id_str =
      area.governorate_id != null && area.governorate_id !== '' ? String(area.governorate_id) : '';
    setFormData(prev => ({
      ...prev,
      area_id: String(area.id),
      slot_id: '',
      governorate_id: governorate_id_str,
    }));
    setErrors(prev => ({ ...prev, area_id: undefined }));
    loadDeliverySlots(String(area.id));
  }, []);

  const updateField = (field: keyof AddressFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validateForm = (): boolean => {
    const next_errors: Record<string, string> = {};
    if (show_field('name') && isAddressFormFieldRequired('name') && !formData.name.trim()) {
      next_errors.name = t('address.pleaseEnterAddressName');
    }
    if (!formData.area_id) {
      next_errors.area_id = t('address.pleaseSelectArea');
    }
    if (show_field('block') && isAddressFormFieldRequired('block') && !formData.block.trim()) {
      next_errors.block = t('address.pleaseEnterBlock');
    }
    if (show_field('street') && isAddressFormFieldRequired('street') && !formData.street.trim()) {
      next_errors.street = t('address.pleaseEnterStreet');
    }
    if (show_field('house_name') && isAddressFormFieldRequired('house_name') && !formData.house_name.trim()) {
      next_errors.house_name = t('address.pleaseEnterHouse');
    }
    if (show_field('phone')) {
      if (!formData.phone.trim()) {
        next_errors.phone = t('address.pleaseEnterPhone');
      } else {
        // `\D` is ASCII-only — convert Eastern digits first or RTL keyboards always fail validation.
        const phone_digits = extractAsciiPhoneDigits(formData.phone);
        if (phone_digits.length < 7) {
          next_errors.phone = t('address.pleaseEnterValidPhone');
        }
      }
    }
    setErrors(next_errors);
    if (Object.keys(next_errors).length > 0) {
      Alert.alert(t('address.validationError'), t('address.pleaseFixErrorsBelow'));
      return false;
    }
    return true;
  };

  const areasArray = Array.isArray(areas) ? areas : [];
  const selectedArea = areasArray.find((a: any) => String(a.id) === formData.area_id);

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    if (!user?.id) {
      Alert.alert(t('common.error'), t('address.userNotAuthenticated'));
      return;
    }

    setIsLoading(true);

    const addressData: AddressPayload = {
      customer_id: user.id,
      name: show_field('name') ? formData.name.trim() : formData.block.trim() || formData.street.trim() || t('address.addressDetails'),
      area_id: parseInt(formData.area_id, 10),
      block: formData.block.trim(),
      street: formData.street.trim(),
      house_name: formData.house_name.trim(),
      phone: toAsciiDigits(formData.phone.trim()),
      is_default: formData.is_default,
    };
    if (formData.floor?.trim()) {addressData.floor = formData.floor.trim();}
    if (formData.flat?.trim()) {addressData.flat = formData.flat.trim();}
    if (formData.landmark?.trim()) {addressData.landmark = formData.landmark.trim();}
    if (formData.avenue?.trim()) {addressData.avenue = formData.avenue.trim();}
    if (formData.driver_note?.trim()) {addressData.driver_note = formData.driver_note.trim();}
    if (formData.PACI_number?.trim()) {addressData.PACI_number = formData.PACI_number.trim();}
    if (formData.zip_code?.trim()) {addressData.zip_code = formData.zip_code.trim();}
    if (formData.slot_id) {addressData.slot_id = parseInt(formData.slot_id, 10);}

    const governorate_from_form = formData.governorate_id.trim();
    const resolved_governorate_id =
      governorate_from_form !== ''
        ? parseInt(governorate_from_form, 10)
        : selectedArea?.governorate_id;
    if (resolved_governorate_id == null || Number.isNaN(resolved_governorate_id)) {
      setIsLoading(false);
      Alert.alert(t('common.error'), t('address.pleaseSelectArea'));
      return;
    }
    addressData.governorate_id = resolved_governorate_id;

    if (isEditing) {
      updateAddressMutation.mutate(
        { addressId: editAddress.id, customerId: user.id, data: addressData },
        {
          onSuccess: () => {
            Alert.alert(t('common.success'), t('address.addressUpdatedSuccess'), [{ text: t('common.ok'), onPress: () => on_back_exit() }]);
          },
          onError: () => {
            Alert.alert(t('common.error'), t('address.failedToUpdateAddress'));
          },
          onSettled: () => setIsLoading(false),
        },
      );
    } else {
      createAddressMutation.mutate(addressData, {
        onSuccess: () => {
          Alert.alert(t('common.success'), t('address.addressAddedSuccess'), [
            {
              text: t('common.ok'),
              onPress: () => {
                if (after_save_go_checkout && !isEditing) {
                  void (async () => {
                    const data = await getSelectedSubscription();
                    if (data && typeof data === 'object') {
                      (navigation as unknown as { replace: (n: string, p: object) => void }).replace('Checkout', { data });
                    } else {
                      on_back_exit();
                    }
                  })();
                } else {
                  on_back_exit();
                }
              },
            },
          ]);
        },
        onError: () => {
          Alert.alert(t('common.error'), t('address.failedToAddAddress'));
        },
        onSettled: () => setIsLoading(false),
      });
    }
  };

  return (
    <StackScreenShell colors={themeColors}>
    <View style={styles.screenRoot}>
      <ScreenHeader title={isEditing ? t('address.formTitleEdit') : t('address.formTitleAdd')} onBack={on_back_exit} scrollY={scrollY} />
      <View style={[layoutStyles.keyboardWrap, styles.contentWrap]}>
        <Animated.ScrollView
          style={[layoutStyles.content, styles.scrollRoot]}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[
            layoutStyles.contentContainer,
            {
              paddingTop: header_top_padding + appTheme.spacing.sm,
              paddingBottom: scroll_padding_bottom,
            },
          ]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
          showsVerticalScrollIndicator={false}
          automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          bounces={false}>
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>{t('address.addressDetails')}</Text>

            {show_field('name') ? (
            <View style={styles.fieldGroup}>
              <Input
                placeholder={t('address.addressNamePlaceholder')}
                value={formData.name}
                onChangeText={v => updateField('name', v)}
                error={errors.name}
                containerStyle={sharedStyles.inputContainerFlush}
              />
            </View>
            ) : null}

            <View style={styles.fieldGroup}>
              <AppPickerTrigger
                value={selectedArea ? getAreaBilingualLabel(selectedArea as Record<string, unknown>) || null : null}
                placeholder={areasError && areasArray.length === 0 ? (t('common.retry') ?? 'Tap to retry') : t('address.selectArea')}
                onPress={() => (areasError && areasArray.length === 0 ? refetchAreas() : setShowAreaPicker(true))}
                disabled={areasLoading}
                loading={areasLoading}
                error={errors.area_id}
                accessibilityLabel={t('address.selectArea')}
                containerStyle={sharedStyles.inputContainerFlush}
              />
              <AreaPickerModal visible={showAreaPicker} onClose={() => setShowAreaPicker(false)} onSelect={onAreaChange} areas={areasArray} selectedAreaId={formData.area_id} />
            </View>

            {deliverySlots.length > 0 && (
              <View style={styles.fieldGroup}>
                <AppPickerTrigger
                  value={(() => {
                    const slot = deliverySlots.find(s => String(s.id) === formData.slot_id);
                    return slot?.delivery_start_time
                      ? `${slot.name ? `${slot.name} - ` : ''} ${slot.delivery_start_time} - ${slot.delivery_end_time}`
                      : null;
                  })()}
                  placeholder={t('address.selectDeliverySlot')}
                  onPress={() => setShowSlotPicker(!showSlotPicker)}
                  accessibilityLabel={t('address.selectDeliverySlot')}
                  containerStyle={sharedStyles.inputContainerFlush}
                />
                {showSlotPicker && (
                  <View style={styles.pickerDropdown}>
                    <ScrollView style={styles.pickerScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                      {deliverySlots.map(slot => (
                        <Clickable
                          key={slot.id}
                          onPress={() => {
                            updateField('slot_id', String(slot.id));
                            setShowSlotPicker(false);
                          }}
                          style={styles.pickerOptionWrap}
                          accessibilityLabel={`${slot.delivery_start_time} - ${slot.delivery_end_time}`}
                          accessibilityRole="button">
                          <View style={[styles.pickerOption, formData.slot_id === String(slot.id) && styles.pickerOptionSelected]}>
                            <Text style={[styles.pickerOptionText, formData.slot_id === String(slot.id) && styles.pickerOptionTextSelected]}>
                            {slot.name} - {slot.delivery_start_time} - {slot.delivery_end_time}
                            </Text>
                          </View>
                        </Clickable>
                      ))}
                    </ScrollView>
                  </View>
                )}
              </View>
            )}

            {show_field('block') || show_field('street') ? (
            <View style={styles.fieldGroup}>
              <View style={styles.row}>
                {show_field('block') ? (
                <Input
                  placeholder={t('address.blockPlaceholder')}
                  value={formData.block}
                  onChangeText={v => updateField('block', v)}
                  error={errors.block}
                  containerStyle={[sharedStyles.inputContainerFlush, styles.rowInput]}
                />
                ) : null}
                {show_field('street') ? (
                <Input
                  placeholder={t('address.streetPlaceholder')}
                  value={formData.street}
                  onChangeText={v => updateField('street', v)}
                  error={errors.street}
                  containerStyle={[sharedStyles.inputContainerFlush, styles.rowInput]}
                />
                ) : null}
              </View>
            </View>
            ) : null}

            {show_field('house_name') ? (
            <View style={styles.fieldGroup}>
              <Input
                placeholder={t('address.housePlaceholder')}
                value={formData.house_name}
                onChangeText={v => updateField('house_name', v)}
                error={errors.house_name}
                containerStyle={sharedStyles.inputContainerFlush}
              />
            </View>
            ) : null}

            {show_field('avenue') ? (
            <View style={styles.fieldGroup}>
              <Input
                placeholder={t('address.avenuePlaceholder')}
                value={formData.avenue}
                onChangeText={v => updateField('avenue', v)}
                containerStyle={sharedStyles.inputContainerFlush}
              />
            </View>
            ) : null}

            {show_field('floor') || show_field('flat') ? (
            <View style={styles.fieldGroup}>
              <View style={styles.row}>
                {show_field('floor') ? (
                <Input
                  placeholder={t('address.floorPlaceholder')}
                  value={formData.floor}
                  onChangeText={v => updateField('floor', v)}
                  containerStyle={[sharedStyles.inputContainerFlush, styles.rowInput]}
                />
                ) : null}
                {show_field('flat') ? (
                <Input
                  placeholder={t('address.flatPlaceholder')}
                  value={formData.flat}
                  onChangeText={v => updateField('flat', v)}
                  containerStyle={[sharedStyles.inputContainerFlush, styles.rowInput]}
                />
                ) : null}
              </View>
            </View>
            ) : null}

            {show_field('landmark') ? (
            <View style={styles.fieldGroup}>
              <Input
                placeholder={t('address.landmarkPlaceholder')}
                value={formData.landmark}
                onChangeText={v => updateField('landmark', v)}
                containerStyle={sharedStyles.inputContainerFlush}
              />
            </View>
            ) : null}

            {show_field('PACI_number') ? (
            <View style={styles.fieldGroup}>
              <Input
                placeholder={t('address.paciPlaceholder')}
                value={formData.PACI_number}
                onChangeText={v => updateField('PACI_number', v)}
                containerStyle={sharedStyles.inputContainerFlush}
              />
            </View>
            ) : null}

            {show_field('zip_code') ? (
            <View style={styles.fieldGroup}>
              <Input
                placeholder={t('address.zipPlaceholder')}
                value={formData.zip_code}
                onChangeText={v => updateField('zip_code', v)}
                containerStyle={sharedStyles.inputContainerFlush}
              />
            </View>
            ) : null}

            {show_field('driver_note') ? (
            <View style={styles.fieldGroup}>
              <Input
                placeholder={t('address.driverNotesPlaceholder')}
                value={formData.driver_note}
                onChangeText={v => updateField('driver_note', v)}
                containerStyle={sharedStyles.inputContainerFlush}
              />
            </View>
            ) : null}
          </View>

          {show_field('phone') ? (
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>{t('address.contact')}</Text>

            <View style={styles.fieldGroup}>
              <Input
                placeholder={t('address.phonePlaceholder')}
                value={formData.phone}
                onChangeText={v => updateField('phone', toAsciiDigits(v))}
                keyboardType="phone-pad"
                error={errors.phone}
                containerStyle={sharedStyles.inputContainerFlush}
              />
            </View>
          </View>
          ) : null}

          <Clickable
            onPress={() => updateField('is_default', !formData.is_default)}
            style={styles.defaultToggleWrap}
            accessibilityLabel={t('address.setAsDefaultDeliveryAddress')}
            accessibilityRole="button">
            <View style={styles.defaultToggle}>
              <View style={[styles.checkbox, formData.is_default && styles.checkboxChecked]}>
                {formData.is_default && <SvgIcon name="check" size={16} color={themeColors.onPrimary} />}
              </View>
              <Text style={styles.defaultToggleText}>{t('address.setAsDefaultDeliveryAddress')}</Text>
            </View>
          </Clickable>
        </Animated.ScrollView>

        <View
          style={[
            styles.pageFooter,
            {
              bottom: footer_bottom_offset,
              paddingBottom: FOOTER_VERTICAL_PAD + insets.bottom,
              minHeight: footer_total_height,
            },
          ]}>
          <View style={styles.pageFooterCtaWrap}>
            <Clickable
              onPress={handleSave}
              disabled={isLoading}
              style={[colored_footer_cta.wrap, styles.footerBtnWrap, { width: '100%' }]}
              accessibilityLabel={isEditing ? t('address.updateAddress') : t('address.saveAddress')}
              accessibilityRole="button">
              <View style={[colored_footer_cta.inner, styles.footerBtnInnerFull, androidFlatSurfaceStyle()]}>
                <SvgIcon name="check" size={22} color={themeColors.onPrimary} />
                {isLoading ? (
                  <ButtonLoader size={18} color={themeColors.onPrimary} />
                ) : (
                  <Text style={colored_footer_cta.text}>{isEditing ? t('address.updateAddress') : t('address.saveAddress')}</Text>
                )}
              </View>
            </Clickable>
          </View>
        </View>
      </View>
    </View>
    </StackScreenShell>
  );
};

export default AddressFormScreen;
